<template>
  <BaseDialog
    :title="$t('shiftCalendarEdit.dialog.batchShift.title')"
    width="610px"
    :style="warning ?`opacity: 0` : ''"
    :btn1-loading="loading"
    @close="$emit('close')"
    @confirm="checkRangeOverride"
  >
    <div>
      <BaseElForm ref="formRef" :model="formData" :rules="formRules" label-position="top">
        <BaseElFormItem :label="$t('shiftCalendarEdit.days.title')" prop="days">
          <BaseElCheckboxGroup v-model="formData.days">
            <div class="options-container">
              <BaseElCheckbox v-for="day in weekdaysOption" :key="day.label" :label="day.week">
                {{ day.label }}<span v-if="day.isDisabled">{{ $t('shiftCalendarEdit.dayoff.title') }}</span>
              </BaseElCheckbox>
            </div>
          </BaseElCheckboxGroup>
        </BaseElFormItem>
        <BaseElFormItem :label="$t('shiftCalendarEdit.dateRange.title')" prop="dateRange">
          <el-date-picker
            v-model="formData.dateRange"
            editable
            type="daterange"
            :range-separator="$t('common.input.datepicker.rangeSeparator')"
            :start-placeholder="$t('common.input.datepicker.startDate.title')"
            :end-placeholder="$t('common.input.datepicker.endDate.title')"
            format="yyyy-MM-dd"
            style="width: 100%"
            :default-time="['00:00:00', '23:59:59']"
            :picker-options="pickerOptions"
          />
        </BaseElFormItem>
      </BaseElForm>
    </div>

    <ScheduleCollapseWarningModal
      v-if="warning"
      @confirm="onConfirm"
      @close="(warning = false, loading = false);"
    />
  </BaseDialog>
</template>

<script>
import { defineComponent, computed, ref, onBeforeMount } from 'vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import ScheduleCollapseWarningModal from './ScheduleCollapseWarningModal.vue'
import { useBaseForm } from '@/use/useForm'
import { GetServiceUnitRangeSchedule, BatchUpdateUnitShiftSchedule } from '@/api/shiftSchedule'
import { get, find } from 'lodash'
import dayjs from '@/lib/dayjs'
import { useShop } from '@/use/shop'
import notifyMessage from '@/config/notifyMessage'
import { noEmptyRules } from '@/validation'
import formUtils from '@/utils/form'
import { i18n } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'BatchCreateShiftScheduleModal',
  components: { BaseDialog, ScheduleCollapseWarningModal },
  props: {
    shiftClassList: { type: Array, default: () => [] },
    unitSchedule: { type: Array, default: () => [] },
    shopBusinessTimeConfig: { type: Object, default: () => ({}) },
    context: { type: Object, default: () => ({}) },
  },
  setup (props, { emit }) {
    const loading = ref(false)
    const warning = ref(false)
    const { initFormData, formRef, formData } = useBaseForm()
    const { shopId } = useShop()

    initFormData({
      days: [],
      dateRange: null,
    })

    const formRules = computed(() => {
      return {
        dateRange: [noEmptyRules()],
        days: [noEmptyRules()],
      }
    })

    const weekdaysOption = computed(() => {
      const weekdaysConfig = get(props.shopBusinessTimeConfig, 'customWeekConfigs') || []
      const weekDays = ['common.week.sun', 'common.week.mon', 'common.week.tue', 'common.week.wed', 'common.week.thu', 'common.week.fri', 'common.week.sat']
      const options = []

      // 如果 shopBusinessTimeConfig.mode === '24Hours'，回傳全部選項
      if (get(props.shopBusinessTimeConfig, 'mode') === '24Hours') {
        for (const idx in weekDays) {
          options.push({
            week: idx,
            label: i18n.t(weekDays[idx]),
            isDisabled: false,
          })
        }
      }

      // 如果 shopBusinessTimeConfig.mode === 'CustomWeek'
      if (get(props.shopBusinessTimeConfig, 'mode') === 'CustomWeek') {
        for (const idx in weekDays) {
          const dayConfig = find(weekdaysConfig, { week: Number(idx) })
          options.push({
            week: idx,
            label: i18n.t(weekDays[idx]),
            isDisabled: !dayConfig,
          })
        }
      }
      // 將第一筆(週日)移到最後
      options.push(options[0])
      options.shift()

      return options
    })

    const pickerOptions = {
      disabledDate: (date) => {
        // 可選 - 往後算三個月 (包含當月)
        const minMonth = dayjs(get(props.context, 'selectDate.date')).month()
        const maxMonth = dayjs(get(props.context, 'selectDate.date')).month() + 2
        const curMonth = dayjs(date).month()
        const fixedYear = dayjs(get(props.context, 'selectDate.date')).year()
        if (dayjs(date).year() < fixedYear || dayjs(date).year() > fixedYear) return date
        if (curMonth < minMonth) return date
        else if (curMonth > maxMonth) return date
      },
    }

    const checkRangeOverride = async () => {
      if (!await formUtils.checkForm(formRef.value)) return
      loading.value = true
      const start = get(formData.dateRange, '[0]')
      const end = get(formData.dateRange, '[1]')
      const [res, err] = await GetServiceUnitRangeSchedule({
        shopId: shopId.value,
        appointmentUnitId: get(props.context, 'serviceUnit.id'),
        weeks: formData.days,
        start: dayjs(start).toDate(),
        end: dayjs(end).toDate(),
      })
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }

      if (get(res, 'hasAppointmentScheduleCount', '').toString() > 0) {
        warning.value = true
        return
      }
      onConfirm()
    }

    const onConfirm = async () => {
      if (loading.value) return
      warning.value = false
      loading.value = true
      const start = get(formData.dateRange, '[0]')
      const end = get(formData.dateRange, '[1]')
      const [, err] = await BatchUpdateUnitShiftSchedule({
        shopId: shopId.value,
        appointmentUnitId: get(props.context, 'serviceUnit.id'),
        appointmentScheduleDayId: get(props.context, 'shiftClass.id'),
        weeks: formData.days,
        start: dayjs(start).toDate(),
        end: dayjs(end).toDate(),
      })
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success(notifyMessage.updateSuccess)
      emit('refresh')
      emit('close')
      loading.value = false
    }

    onBeforeMount(() => {
      const selectDate = get(props.context, 'selectDate')
      const selectDateWeekDay = dayjs(selectDate.dateStr).isoWeekday()
      formData.days.push(selectDateWeekDay === 7 ? '0' : selectDateWeekDay.toString())
    })

    return {
      formRef,
      formData,
      formRules,
      weekdaysOption,
      onConfirm,
      loading,
      pickerOptions,
      warning,
      checkRangeOverride,
    }
  },
})
</script>

<style lang="postcss" scoped>

</style>
